import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Products.css';
import { toast } from 'react-toastify';
import { Search, Trash2, X, ImageOff, Package, BarChart3, Plus, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { productsAPI } from '../../config/product-api';
import { helpers } from '../../utils/helpers';
import { formatPrice, getPriceInputLabel } from '../../utils/orderDisplay';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import { getAdminRegion } from '../../constants/stores';
import DeleteConfirmationModal from '../Common/DeleteConfirmationModal/DeleteConfirmationModal';
import { finalizeProductImageFile, createUniqueFileName } from '../../utils/imageCompression';
import {
  MAX_PRODUCT_VIDEO_BYTES,
  createProductVideoCompressor,
} from '../../utils/productVideoCompression';
import {
  buildProductImagesSubmitPayload,
  buildProductVideosSubmitPayload,
  buildVariantsSubmitPayload,
  mapProductImagesFromApi,
  mapProductVideosFromApi,
  mapProductVariantsFromApi,
  validateVariants,
  withProductImageOrder,
} from '../../utils/productVariants';
import ProductVariantsEditor from './ProductVariantsEditor';
import ImageCropperModal from '../Common/ImageCropperModal/ImageCropperModal';
import { PRODUCT_IMAGE_ASPECT_RATIO } from '../../constants/productImage';

const MAX_PRODUCT_IMAGES = 10;
const MAX_PRODUCT_VIDEOS = 3;

const emptyForm = {
  name: '',
  description: '',
  price: '',
  stock: '0',
  isAvailable: true,
  images: [],
  videos: [],
  productId: null,
  variants: [],
  region: 'qatar',
};

const revokeBlobUrls = (mediaList = []) => {
  mediaList.forEach((item) => {
    if (item?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url);
    }
    if (item?.previewUrl?.startsWith('blob:') && item.previewUrl !== item.url) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
};

const Products = ({ adminData, openProductId = null, onOpenProductConsumed }) => {
  const navigate = useNavigate();
  const storeRegion = getAdminRegion(adminData);
  // AbleMart ProductList / ReelVideos: keep ffmpeg on a ref for the page lifetime
  const videoCompressorRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const [isCompressingVideo, setIsCompressingVideo] = useState(false);
  const [videoCompressProgress, setVideoCompressProgress] = useState(0);
  const [cropQueue, setCropQueue] = useState(null);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverImageIndex, setDragOverImageIndex] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null, productName: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_PRODUCTS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_PRODUCTS);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_PRODUCTS);
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_PRODUCTS);
  const canModifyForm = (!editMode && canCreate) || (editMode && canEdit);
  const canViewOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);
  const canViewReports = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_REPORTS)
    || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);
  const canEditStock = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_STOCK);

  const handleViewOrders = () => {
    if (!selectedProductId || !formData.name) return;
    const filter = {
      productId: selectedProductId,
      productNumericId: formData.productId,
      productName: formData.name,
    };
    handleCloseDialog();
    navigate('/dashboard', {
      state: {
        page: 'orders',
        productFilter: filter,
      },
    });
  };

  const handleViewDailySaleReport = () => {
    if (!selectedProductId || !formData.name) return;
    const filter = {
      productId: selectedProductId,
      productNumericId: formData.productId,
      productName: formData.name,
    };
    handleCloseDialog();
    navigate('/dashboard', {
      state: {
        page: 'reports',
        productFilter: filter,
      },
    });
  };

  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await productsAPI.getProductsList({
        page,
        limit: 20,
        search: searchQuery || undefined,
        isAvailable: statusFilter || undefined,
        region: storeRegion,
      });

      if (response.success) {
        setProducts(response.data || []);
        setPagination(response.pagination || pagination);
      } else {
        setError(response.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Error fetching products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isDialogOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDialogOpen]);

  useEffect(() => () => {
    videoCompressorRef.current?.terminate?.();
    videoCompressorRef.current = null;
  }, []);

  useEffect(() => {
    if (canView) {
      fetchProducts(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, currentPage, searchQuery, statusFilter, storeRegion]);

  useEffect(() => {
    if (!openProductId || !canView) return;

    let cancelled = false;

    const openFromList = async () => {
      const fromList = products.find((p) => String(p._id) === String(openProductId));
      if (fromList) {
        if (!cancelled) {
          handleEdit(fromList);
          onOpenProductConsumed?.();
        }
        return;
      }

      // Wait for the first products page load before falling back to a full fetch.
      if (loading) return;

      try {
        const response = await productsAPI.getProductsList({ page: 1, limit: 500, region: storeRegion });
        if (cancelled) return;
        if (response.success) {
          const match = (response.data || []).find((p) => String(p._id) === String(openProductId));
          if (match) {
            handleEdit(match);
          } else {
            toast.error('Product not found');
          }
        } else {
          toast.error(response.error || 'Failed to open product details');
        }
      } catch {
        if (!cancelled) toast.error('Failed to open product details');
      } finally {
        if (!cancelled) onOpenProductConsumed?.();
      }
    };

    openFromList();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProductId, canView, products, loading]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchQuery(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'stock') {
      const cleaned = value === '' ? '' : value.replace(/[^\d]/g, '');
      setFormData((prev) => ({ ...prev, stock: cleaned }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddNew = () => {
    if (!canCreate) return;
    setEditMode(false);
    setSelectedProductId(null);
    setFormData({ ...emptyForm, region: storeRegion });
    setIsDialogOpen(true);
  };

  const handleEdit = (product) => {
    if (!canView) return;
    setEditMode(true);
    setSelectedProductId(product._id);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.rate !== undefined ? product.rate.toString() : '',
      stock: product.stock !== undefined && product.stock !== null ? String(product.stock) : '0',
      isAvailable: product.isAvailable !== false,
      images: mapProductImagesFromApi(product.images),
      videos: mapProductVideosFromApi(product.videos),
      productId: product.productId || null,
      variants: mapProductVariantsFromApi(product.variants),
      region: product.region === 'india' ? 'india' : 'qatar',
    });
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (product) => {
    if (!canDelete) return;
    setDeleteModal({
      isOpen: true,
      productId: product._id,
      productName: product.name || 'this product',
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.productId) return;

    const deletedProductId = deleteModal.productId;
    const shouldCloseDialog = isDialogOpen && selectedProductId === deletedProductId;

    try {
      setIsDeleting(true);
      const response = await productsAPI.deleteProduct(deletedProductId);
      if (response.success) {
        toast.success('Product deleted successfully');
        setDeleteModal({ isOpen: false, productId: null, productName: '' });
        if (shouldCloseDialog) {
          handleCloseDialog();
        }
        fetchProducts(currentPage);
      } else {
        toast.error(response.error || 'Failed to delete product');
      }
    } catch {
      toast.error('An error occurred while deleting the product');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDialog = () => {
    revokeBlobUrls(formData.images);
    revokeBlobUrls(formData.videos);
    setCropQueue(null);
    setIsCompressingImage(false);
    setIsCompressingVideo(false);
    setIsDialogOpen(false);
    setEditMode(false);
    setSelectedProductId(null);
    setFormData(emptyForm);
  };

  const handleVideoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const currentCount = formData.videos?.length || 0;
    if (currentCount + files.length > MAX_PRODUCT_VIDEOS) {
      toast.error(`Maximum ${MAX_PRODUCT_VIDEOS} videos allowed`);
      return;
    }

    if (!videoCompressorRef.current) {
      videoCompressorRef.current = createProductVideoCompressor();
    }
    const compressor = videoCompressorRef.current;

    setIsCompressingVideo(true);
    setVideoCompressProgress(10);
    const nextVideos = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        if (!file.type?.startsWith('video/')) {
          toast.error('Please select a valid video file');
          continue;
        }
        if (file.size > MAX_PRODUCT_VIDEO_BYTES) {
          toast.error('Video exceeds the 10MB limit');
          continue;
        }

        try {
          setVideoCompressProgress(15);
          const processed = await compressor.processProductVideoFile(file, {
            onProgress: (pct) => {
              // Map ffmpeg 0–100 into the add-button label
              setVideoCompressProgress(Math.min(99, Math.max(15, pct)));
            },
          });

          nextVideos.push({
            type: 'new',
            mediaType: 'video',
            file: processed.file,
            thumbnailFile: processed.thumbnailFile || null,
            url: processed.previewUrl,
            previewUrl: processed.previewUrl,
            thumbnail: processed.thumbnailUrl || '',
            order: String(currentCount + nextVideos.length),
          });
        } catch (err) {
          console.error('Product video processing error:', err);
          const msg = String(err?.message || '');
          if (msg.includes('10MB') || msg.includes('5MB')) {
            toast.error(msg.replace('5MB', '10MB'));
          } else if (
            err?.name === 'SecurityError'
            || /Worker|FFmpeg|compressor/i.test(msg)
          ) {
            toast.error('Video compressor failed to load. Refresh and try again.');
          } else {
            toast.error('Video compression failed. Try an MP4 under 10MB.');
          }
        }
      }

      if (nextVideos.length) {
        setFormData((prev) => ({
          ...prev,
          videos: [...(prev.videos || []), ...nextVideos],
        }));
        setVideoCompressProgress(100);
        toast.success(
          nextVideos.length === 1 ? 'Video ready' : `${nextVideos.length} videos ready`,
        );
      }
    } finally {
      setIsCompressingVideo(false);
      setVideoCompressProgress(0);
    }
  };

  const handleRemoveVideo = (index) => {
    setFormData((prev) => {
      const list = [...(prev.videos || [])];
      const [removed] = list.splice(index, 1);
      if (removed?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl);
      } else if (removed?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.url);
      }
      if (removed?.thumbnail?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.thumbnail);
      }
      return { ...prev, videos: list };
    });
  };

  const openNextCrop = (files) => {
    const [first, ...rest] = files;
    if (!first) {
      setCropQueue(null);
      setIsCompressingImage(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      setCropQueue({
        // Fresh key forces a clean cropper remount for each selected image.
        key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        src: readerEvent.target.result,
        remaining: rest,
      });
    };
    reader.readAsDataURL(first);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || cropQueue || isCompressingImage) return;

    const currentCount = formData.images?.length || 0;
    if (currentCount + files.length > MAX_PRODUCT_IMAGES) {
      toast.error(`Maximum ${MAX_PRODUCT_IMAGES} images allowed`);
      return;
    }

    const invalid = files.find((file) => !file.type?.startsWith('image/') || file.size > 8 * 1024 * 1024);
    if (invalid && !invalid.type?.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (invalid) {
      toast.error('Each source image must be less than 8MB');
      return;
    }

    setIsCompressingImage(true);
    openNextCrop(files);
  };

  const handleCropperClose = () => {
    setCropQueue(null);
    setIsCompressingImage(false);
  };

  const handleCropComplete = async (croppedFile) => {
    if (!cropQueue) return;
    const { remaining } = cropQueue;
    setCropQueue(null);
    setIsCompressingImage(true);
    try {
      const finalImage = await finalizeProductImageFile(croppedFile);
      const namedImage = new File(
        [finalImage],
        createUniqueFileName(finalImage.name || 'product.jpg'),
        { type: finalImage.type || 'image/jpeg', lastModified: Date.now() }
      );
      const previewUrl = URL.createObjectURL(namedImage);
      setFormData((prev) => {
        const current = prev.images || [];
        return {
          ...prev,
          images: withProductImageOrder([
            ...current,
            {
              type: 'new',
              mediaType: 'image',
              url: previewUrl,
              thumbnail: previewUrl,
              file: namedImage,
            },
          ]),
        };
      });
      if (remaining.length) {
        openNextCrop(remaining);
      } else {
        setIsCompressingImage(false);
      }
    } catch (err) {
      setIsCompressingImage(false);
      toast.error(err.message || 'Failed to process image');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const mediaItem = prev.images?.[index];
      if (mediaItem?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(mediaItem.url);
      }
      return {
        ...prev,
        images: withProductImageOrder((prev.images || []).filter((_, i) => i !== index)),
      };
    });
  };

  const moveProductImage = (fromIndex, toIndex) => {
    setFormData((prev) => {
      const list = [...(prev.images || [])];
      if (
        fromIndex < 0
        || toIndex < 0
        || fromIndex >= list.length
        || toIndex >= list.length
        || fromIndex === toIndex
      ) {
        return prev;
      }
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      return { ...prev, images: withProductImageOrder(list) };
    });
  };

  const handleImageDragStart = (e, index) => {
    if (!canModifyForm || submitting || isCompressingImage) return;
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    if (draggedImageIndex === null || draggedImageIndex === index) return;
    setDragOverImageIndex(index);
  };

  const handleImageDrop = (e, index) => {
    e.preventDefault();
    if (draggedImageIndex === null || draggedImageIndex === index) {
      setDraggedImageIndex(null);
      setDragOverImageIndex(null);
      return;
    }
    moveProductImage(draggedImageIndex, index);
    setDraggedImageIndex(null);
    setDragOverImageIndex(null);
  };

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverImageIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editMode && !canEdit) return;
    if (!editMode && !canCreate) return;

    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    if (formData.price === '' || isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      toast.error('Price must be a valid number greater than 0');
      return;
    }

    if (canEditStock) {
      const stockNum = Number(formData.stock);
      if (formData.stock === '' || isNaN(stockNum) || stockNum < 0 || !Number.isInteger(stockNum)) {
        toast.error('Stock must be a whole number of 0 or more');
        return;
      }
    }

    const variantError = validateVariants(formData.variants || []);
    if (variantError) {
      toast.error(variantError);
      return;
    }

    setSubmitting(true);
    try {
      const { imagesData, productImageFiles } = buildProductImagesSubmitPayload(formData.images || []);
      const {
        videosData,
        productVideoFiles,
        productVideoThumbnailFiles,
      } = buildProductVideosSubmitPayload(formData.videos || []);
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        rate: Number(formData.price),
        isAvailable: formData.isAvailable,
        imagesData,
        productImageFiles,
        videosData,
        productVideoFiles,
        productVideoThumbnailFiles,
        region: storeRegion,
      };

      if (canEditStock) {
        payload.stock = Math.max(0, Math.floor(Number(formData.stock)) || 0);
      }

      if (editMode || (formData.variants || []).length > 0) {
        const { variantsData, variantImageFiles } = buildVariantsSubmitPayload(formData.variants || []);
        payload.variantsData = variantsData;
        payload.variantImageFiles = variantImageFiles;
      }

      let result;
      if (editMode) {
        result = await productsAPI.updateProduct(selectedProductId, payload);
      } else {
        result = await productsAPI.createProduct(payload);
      }

      if (result.success) {
        toast.success(editMode ? 'Product updated successfully' : 'Product created successfully');
        handleCloseDialog();
        fetchProducts(currentPage);
      } else {
        toast.error(result.error || (editMode ? 'Failed to update product' : 'Failed to create product'));
      }
    } catch (err) {
      toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} the product`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canView) {
    return (
      <div className="productlist-page">
        <div className="productlist-empty-state">
          <p>You do not have permission to view products.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="productlist-page">
      <div className="productlist-page-header">
        <div className="productlist-page-title">
          <h1>Products</h1>
          <p>View and manage all your products</p>
        </div>
        {canCreate && (
          <button className="productlist-create-btn" onClick={handleAddNew}>
            Add Product
          </button>
        )}
      </div>

      <div className="productlist-search-bar-container">
        <form className="productlist-search-form" onSubmit={handleSearch}>
          <select
            className="productlist-status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="true">In Stock</option>
            <option value="false">Out of Stock</option>
          </select>
          <div className="productlist-search-input-wrapper">
            <Search size={18} className="productlist-search-icon" />
            <input
              type="text"
              className="productlist-search-input"
              placeholder="Search by name or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <button type="submit" className="productlist-search-btn">Search</button>
          {searchQuery && (
            <button type="button" className="productlist-reset-btn" onClick={handleResetSearch}>
              <X size={18} />
            </button>
          )}
        </form>
      </div>

      <div className="productlist-table-container">
        {loading ? (
          <div className="productlist-loading-state">
            <div className="productlist-loading-spinner" />
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="productlist-error-state">
            <p>{error}</p>
            <button onClick={() => fetchProducts(currentPage)} className="productlist-retry-button">
              Retry
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="productlist-empty-state">
            <p>No products found</p>
          </div>
        ) : (
          <>
            <div className="productlist-table-wrapper">
              <table className="productlist-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th className="productlist-stock-cell">Stock</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const thumbnailUrl = product.images?.[0]?.thumbnail || product.images?.[0]?.image || '';
                    return (
                      <tr key={product._id}>
                        <td className="productlist-pic-cell">
                          <div className="productlist-table-pic-wrapper">
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt={product.name}
                                className="productlist-table-pic"
                              />
                            ) : (
                              <ImageOff size={20} className="productlist-table-pic-fallback" />
                            )}
                          </div>
                        </td>
                        <td className="productlist-id-cell">
                          {canView ? (
                            <span className="productlist-id-link" onClick={() => handleEdit(product)}>
                              {product.productId}
                            </span>
                          ) : (
                            <span>{product.productId}</span>
                          )}
                        </td>
                        <td className="productlist-name-cell">
                          {product.name}
                        </td>
                        <td>
                          <span className={`productlist-status-badge ${product.isAvailable !== false ? 'active' : 'inactive'}`}>
                            {product.isAvailable !== false ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="productlist-stock-cell">{product.stock ?? 0}</td>
                        <td className="productlist-price-cell">{formatPrice(product.rate || 0, adminData)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="productlist-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev || loading}
                  className="productlist-pagination-button"
                >
                  Previous
                </button>
                <span className="productlist-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext || loading}
                  className="productlist-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isDialogOpen && ((editMode && canView) || (!editMode && canCreate)) && (
        <div className="productlist-dialog-overlay">
          <div className="productlist-dialog-box">
            <div className="productlist-dialog-header">
              <button type="button" className="productlist-close-btn" onClick={handleCloseDialog} aria-label="Close">&times;</button>
              <div className="productlist-dialog-header-row">
                <div className="productlist-dialog-title-section">
                  <h2>
                    {editMode
                      ? `${canEdit ? 'Edit Product' : 'Product Details'}${formData.name ? ` - ${formData.name}` : ''}`
                      : 'Add Product'}
                  </h2>
                  {editMode && formData.productId && (
                    <p className="productlist-dialog-subtitle">Product ID: {formData.productId}</p>
                  )}
                </div>
                <div className="availability-toggle-container productlist-dialog-status">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={formData.isAvailable}
                      onChange={handleInputChange}
                      disabled={!canEdit || submitting}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`availability-text ${formData.isAvailable ? 'in-stock' : 'out-of-stock'}`}>
                    {formData.isAvailable ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="productlist-form">
              <span className="productlist-section-label">Product Images (1:1, max 1MB each)</span>
              {canModifyForm && (formData.images || []).length > 1 && (
                <p className="productlist-image-reorder-hint">Drag images or use the arrows to rearrange. First image is the main photo.</p>
              )}
              <div className="productlist-image-section">
                <div className="productlist-images-grid">
                  {(formData.images || []).map((media, index) => (
                    <div
                      key={media.url || media.thumbnail || `product-image-${index}`}
                      className={[
                        'productlist-image-item',
                        canModifyForm ? 'productlist-image-item-draggable' : '',
                        draggedImageIndex === index ? 'dragging' : '',
                        dragOverImageIndex === index ? 'drag-over' : '',
                        index === 0 ? 'is-primary' : '',
                      ].filter(Boolean).join(' ')}
                      draggable={canModifyForm && !submitting && !isCompressingImage}
                      onDragStart={(e) => handleImageDragStart(e, index)}
                      onDragOver={(e) => handleImageDragOver(e, index)}
                      onDrop={(e) => handleImageDrop(e, index)}
                      onDragEnd={handleImageDragEnd}
                      title={index === 0 ? 'Main product image' : `Image ${index + 1}`}
                    >
                      <img src={media.thumbnail || media.url} alt={`Product ${index + 1}`} draggable={false} />
                      {index === 0 && <span className="productlist-image-primary-badge">Main</span>}
                      {canModifyForm && (
                        <>
                          <span className="productlist-image-drag-handle" title="Drag to rearrange">
                            <GripVertical size={14} />
                          </span>
                          <div className="productlist-image-reorder-btns">
                            <button
                              type="button"
                              className="productlist-image-move-btn"
                              onClick={() => moveProductImage(index, index - 1)}
                              disabled={index === 0 || submitting || isCompressingImage}
                              title="Move left"
                            >
                              <ChevronLeft size={14} />
                            </button>
                            <button
                              type="button"
                              className="productlist-image-move-btn"
                              onClick={() => moveProductImage(index, index + 1)}
                              disabled={index === (formData.images || []).length - 1 || submitting || isCompressingImage}
                              title="Move right"
                            >
                              <ChevronRight size={14} />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="productlist-image-remove"
                            onClick={() => handleRemoveImage(index)}
                            disabled={submitting || isCompressingImage}
                            title="Remove image"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {canModifyForm && (formData.images || []).length < MAX_PRODUCT_IMAGES && (
                    <label
                      className={`productlist-image-add ${isCompressingImage ? 'loading' : ''}`}
                      title="Add image"
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageSelect}
                        disabled={!canModifyForm || isCompressingImage || submitting}
                        style={{ display: 'none' }}
                      />
                      {isCompressingImage ? '...' : <Plus size={28} />}
                    </label>
                  )}
                  {!canModifyForm && !(formData.images || []).length && (
                    <div className="productlist-image-item productlist-image-empty">
                      <ImageOff size={28} />
                      <span>No images</span>
                    </div>
                  )}
                </div>
              </div>

              <span className="productlist-section-label">
                Product Videos (optional, max 10MB each)
              </span>
              <div className="productlist-image-section">
                <div className="productlist-images-grid">
                  {(formData.videos || []).map((media, index) => {
                    const poster = media.thumbnail || '';
                    const videoSrc = media.previewUrl || media.url || '';
                    const posterIsImage =
                      poster &&
                      (media.thumbnailFile?.type?.startsWith('image/') ||
                        /\.(jpe?g|png|webp|gif)(\?|$)/i.test(poster) ||
                        (poster.startsWith('blob:') && media.thumbnailFile?.type?.startsWith('image/')));
                    // AbleMart preview: muted looping <video> (poster optional)
                    return (
                      <div
                        key={videoSrc || poster || `product-video-${index}`}
                        className="productlist-image-item"
                      >
                        <video
                          src={videoSrc}
                          poster={posterIsImage ? poster : undefined}
                          className="productlist-video-thumb"
                          muted
                          playsInline
                          autoPlay
                          loop
                          preload="metadata"
                        />
                        <span className="productlist-image-primary-badge">Video</span>
                        {canModifyForm && (
                          <button
                            type="button"
                            className="productlist-image-remove"
                            onClick={() => handleRemoveVideo(index)}
                            disabled={submitting || isCompressingVideo}
                            title="Remove video"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {canModifyForm && (formData.videos || []).length < MAX_PRODUCT_VIDEOS && (
                    <label
                      className={`productlist-image-add ${isCompressingVideo ? 'loading' : ''}`}
                      title="Add video"
                    >
                      <input
                        type="file"
                        multiple
                        accept="video/mp4,video/webm,video/quicktime,video/*"
                        onChange={handleVideoSelect}
                        disabled={!canModifyForm || submitting || isCompressingVideo}
                        style={{ display: 'none' }}
                      />
                      {isCompressingVideo
                        ? (videoCompressProgress > 0 ? `${videoCompressProgress}%` : '...')
                        : <Plus size={28} />}
                    </label>
                  )}
                  {!canModifyForm && !(formData.videos || []).length && (
                    <div className="productlist-image-item productlist-image-empty">
                      <ImageOff size={28} />
                      <span>No videos</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="productlist-form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Product name"
                  required
                  disabled={!canEdit || submitting}
                  readOnly={!canEdit}
                />
              </div>

              <div className="productlist-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Product description..."
                  rows={3}
                  disabled={!canEdit || submitting}
                  readOnly={!canEdit}
                />
              </div>

              <div className="productlist-form-row">
                <div className="productlist-form-group productlist-form-group-half">
                  <label htmlFor="price">{getPriceInputLabel(adminData)} *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    step="0.01"
                    min="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                    disabled={!canEdit || submitting}
                    readOnly={!canEdit}
                  />
                </div>

                <div className="productlist-form-group productlist-form-group-half">
                  <label htmlFor="stock">Stock{canEditStock ? '' : ' (view only)'}</label>
                  <input
                    type="number"
                    id="stock"
                    name="stock"
                    step="1"
                    min="0"
                    value={formData.stock}
                    onChange={handleInputChange}
                    placeholder="0"
                    disabled={!canEditStock || submitting}
                    readOnly={!canEditStock}
                  />
                </div>
              </div>

              <ProductVariantsEditor
                variants={formData.variants || []}
                onChange={(variants) => setFormData((prev) => ({ ...prev, variants }))}
                disabled={submitting || isCompressingImage || !canEdit}
                basePrice={formData.price}
                adminData={adminData}
              />

               <div className="productlist-form-actions">
                 <div className="productlist-form-actions-left">
                   {editMode && canViewOrders && (
                     <button
                       type="button"
                       className="productlist-view-orders-btn"
                       onClick={handleViewOrders}
                       disabled={submitting}
                     >
                       <Package size={16} />
                       View Orders
                     </button>
                   )}
                   {editMode && canViewReports && (
                     <button
                       type="button"
                       className="productlist-view-orders-btn productlist-view-report-btn"
                       onClick={handleViewDailySaleReport}
                       disabled={submitting}
                     >
                       <BarChart3 size={16} />
                       View Daily Sale Report
                     </button>
                   )}
                   {editMode && canDelete && (
                     <button
                       type="button"
                       className="productlist-delete-dialog-btn"
                       onClick={() => handleDeleteClick({ _id: selectedProductId, name: formData.name })}
                       disabled={submitting}
                     >
                       <Trash2 size={16} />
                       Delete Product
                     </button>
                   )}
                 </div>
                 <button type="button" className="productlist-cancel-btn" onClick={handleCloseDialog} disabled={submitting}>
                   Cancel
                 </button>
                 {(!editMode || canEdit) && (
                   <button type="submit" className="productlist-submit-btn" disabled={submitting || (editMode && !canEdit)}>
                     {submitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Product' : 'Create Product')}
                   </button>
                 )}
               </div>
            </form>
          </div>
        </div>
      )}

      {cropQueue?.src && (
        <ImageCropperModal
          key={cropQueue.key || cropQueue.src}
          imageSrc={cropQueue.src}
          aspectRatio={PRODUCT_IMAGE_ASPECT_RATIO}
          title="Crop product image (1:1)"
          onClose={handleCropperClose}
          onCropComplete={handleCropComplete}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, productId: null, productName: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        message={`Are you sure you want to delete "${deleteModal.productName}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Products;
